import { JSX, useState, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { queryClient, PERSISTER_KEY } from './queryClient';

/**
 * Composant QueryProvider.
 * @param children - Les enfants à rendre.
 * @returns Le composant QueryProvider.
 */
const QueryProvider = ({ children }: { children: JSX.Element }) => {
  const [showDevtools, setShowDevtools] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Shift+Q pour afficher/masquer les Devtools
      if (event.ctrlKey && event.shiftKey && event.key === 'Q') {
        event.preventDefault();
        setShowDevtools((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Persistance du cache (cross-reload)
  const persister = createAsyncStoragePersister({
    storage: window.localStorage,
    key: PERSISTER_KEY
  });

  persistQueryClient({
    queryClient,
    persister,
    maxAge: 24 * 60 * 60 * 1000, // 24h avant d'ignorer l'hydratation
    buster: `${process.env.REACT_APP_VERSION}-buster`, // invalide tout le cache si version change
    dehydrateOptions: {
      // Filtre les queries à persister (sécurité / taille)
      shouldDehydrateQuery: (query) => {
        const key = JSON.stringify(query.queryKey);
        // Exclure auth/sensible et éviter les très grosses entrées
        if (key.includes('auth') || key.includes('token')) return false;
        // Ne persiste que les queries réussies
        if (query.state.status !== 'success') return false;
        return true;
      }
    }
  });

  const isDev = process.env.NODE_ENV === 'development';

  return (
    <QueryClientProvider client={queryClient}>
      {children}

      {isDev && showDevtools && (
        <TanStackDevtools config={{ hideUntilHover: false }} eventBusConfig={{ debug: true }} />
      )}
    </QueryClientProvider>
  );
};

export default QueryProvider;
